import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SceltaCliente from "@/components/common/SceltaCliente";
import { eur } from "@/lib/formato";
import { erroreDi } from "@/lib/crm";
import { riepilogoLista } from "@/lib/listini";

/* «Salva la lista»: la lista di Aleandro finisce nella scheda del cliente come PDF, oppure si scarica soltanto.
   Salva nella scheda chi può modificarla (titolare e venditore); l'amministrazione scarica. Il PDF lo fa il server
   dalle voci: nel PDF ci sono solo prezzi di listino, mai un prezzo d'acquisto. */
export default function DialogoLista({ lista, onChiudi }) {
  const { profile } = useAuth() || {};
  const puoSalvare = profile?.ruolo === "titolare" || profile?.ruolo === "venditore";
  const [cliente, setCliente] = useState(null);
  const [lavoro, setLavoro] = useState(null); // "salva" | "pdf"
  const [salvata, setSalvata] = useState(null);
  const r = riepilogoLista(lista);

  async function salva() {
    setLavoro("salva");
    try {
      const { data } = await api.post("/listini/liste", { ...lista, cliente_id: cliente.id });
      setSalvata({ cliente, nome: data.nome });
    } catch (e) {
      toast.error(erroreDi(e));
    } finally {
      setLavoro(null);
    }
  }

  async function scarica() {
    setLavoro("pdf");
    try {
      const { data } = await api.post("/listini/pdf", lista, { responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: "lista-listini.pdf" });
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch (e) {
      toast.error("Il PDF non è partito. Riprova fra un momento.");
    } finally {
      setLavoro(null);
    }
  }

  return (
    <Dialog open onOpenChange={(aperto) => !aperto && onChiudi()}>
      <DialogContent data-testid="listini-dialogo-lista" className="max-h-[92dvh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Salva la lista</DialogTitle>
          <DialogDescription>
            {r.voci} {r.voci === 1 ? "voce" : "voci"}, {eur(r.listino, { decimali: 0 })} di listino, IVA esclusa
            {lista.iva ? ` (${eur(r.conIva, { decimali: 0 })} con IVA)` : ""}.
          </DialogDescription>
        </DialogHeader>

        {salvata ? (
          <div className="grid gap-group" data-testid="listini-salvata" role="status">
            <p className="flex items-start gap-inside text-body text-ink">
              <Check className="mt-0.5 size-5 shrink-0 text-success" aria-hidden="true" />
              <span>Salvata fra i documenti di <b>{salvata.cliente.nome}</b>.</span>
            </p>
            <DialogFooter className="gap-inside">
              <Button variant="secondary" onClick={onChiudi}>Chiudi</Button>
              <Button asChild>
                <Link to={`/clienti/${salvata.cliente.id}`} onClick={onChiudi} data-testid="listini-apri-scheda">Apri la scheda</Link>
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="grid gap-card">
            {puoSalvare ? (
              <div className="grid gap-inside">
                <label className="text-small font-semibold text-ink-strong">Per quale cliente</label>
                <SceltaCliente valore={cliente?.id} onScegli={setCliente} testId="listini-cliente" />
              </div>
            ) : (
              <p className="text-small text-ink-2" data-testid="listini-solo-pdf">
                Nella scheda del cliente la lista la salva chi lo segue. Da qui puoi scaricare il PDF.
              </p>
            )}
            <DialogFooter className="gap-inside">
              <Button variant="secondary" onClick={scarica} disabled={lavoro !== null} aria-busy={lavoro === "pdf"} data-testid="listini-scarica">
                <Download aria-hidden="true" />
                Scarica il PDF
              </Button>
              {puoSalvare ? (
                <Button onClick={salva} disabled={!cliente || lavoro !== null} aria-busy={lavoro === "salva"} data-testid="listini-salva">
                  Salva nella scheda
                </Button>
              ) : null}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
